// 上の帯の兵糧を押すと開く小窓：本体と予備・次の回復・今日あと回復する量・満タンまで・上限の内訳・出撃できる回数。
// 計算はサーバー（core の meta/stamina.ts）と同じ決まり：4分に1回復、本体が上限なら予備へ。月パス中は3倍速で、1日（JST）の自然回復は dailyRegen まで。
// 画面の兵糧（state.stamina）を手元で進めて出す。次にサーバーから受け取れば揃う。
import { META_CONFIG, levelTier, monthlyActive, stageStaminaCost } from '@hyakki/core';
import { state, levelNow } from '../state';
import { advanceStamina } from '../stamina-tick';
import { esc, fmt, hhmm, resIcon } from './dom';
import { openModal } from './overlay';

const JST = 9 * 3_600_000;
const DAY = 86_400_000;
const jstDay = (t: number) => new Date(t + JST).toISOString().slice(0, 10);
const nextMidnight = (t: number) => Math.floor((t + JST) / DAY) * DAY + DAY - JST;

/** 「あと 2時間15分」 */
function left(ms: number) {
  if (ms <= 0) return 'まもなく';
  const m = Math.ceil(ms / 60_000);
  if (m < 60) return `あと${m}分`;
  const h = Math.floor(m / 60);
  return h >= 48 ? `あと${Math.floor(h / 24)}日${h % 24}時間` : `あと${h}時間${m % 60 ? `${m % 60}分` : ''}`;
}
const mmss = (ms: number) => {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};
const at = (t: number) => (jstDay(t) === jstDay(Date.now()) ? hhmm(t) : `${new Date(t).getMonth() + 1}/${new Date(t).getDate()} ${hhmm(t)}`);

/** 今の兵糧から、回復を1つずつ進めて「今日あと何回復するか」「本体・予備が満タンになる時刻」を出す（月パスの1日の上限も数える） */
function forecast(now: number) {
  const st0 = state.stamina!;
  const p = state.profile;
  const base = META_CONFIG.stamina.regenMinutes * 60_000;
  const st = advanceStamina(st0, now, base);
  const fast = !!p && monthlyActive(p, now);
  const per = fast ? base / META_CONFIG.monthly.regenSpeed : base;
  const cap = META_CONFIG.monthly.dailyRegen;
  const today = jstDay(now);
  // 月パスの今日の回復の数（プロフィールの兵糧の記録。出撃のあとは少し古いことがある）
  const rec = p?.stamina;
  const gotToday = fast && rec?.day === today ? (rec.got ?? 0) : 0;
  const midnight = nextMidnight(now);
  let { value, reserve } = st;
  let t = st.nextAt;
  let day = today;
  let got = gotToday;
  let todayLeft = 0;
  let mainFull: number | null = value >= st.max ? now : null;
  let reserveFull: number | null = reserve >= st.reserveMax ? now : null;
  // 予備まで満タン、またはおよそ10日先で打ち切る
  for (let i = 0; t != null && i < 20_000 && (value < st.max || reserve < st.reserveMax) && t < now + 10 * DAY; i++) {
    if (fast && p?.monthlyUntil && t > p.monthlyUntil) break; // 月パスが切れた先は概算しない
    if (fast) {
      const d = jstDay(t);
      if (d !== day) {
        day = d;
        got = 0;
      }
      if (got >= cap) {
        t = nextMidnight(t) + per;
        continue;
      }
      got++;
    }
    if (value < st.max) value++;
    else reserve++;
    if (t < midnight) todayLeft++;
    if (mainFull == null && value >= st.max) mainFull = t;
    if (reserveFull == null && reserve >= st.reserveMax) reserveFull = t;
    t += per;
  }
  return { st, fast, per, cap, gotToday, todayLeft, mainFull, reserveFull };
}

function html(now: number) {
  const p = state.profile;
  const f = forecast(now);
  const { st } = f;
  const S = META_CONFIG.stamina;
  const kura = p?.village?.levels.hyorogura ?? 0;
  const paid = (p?.entitlements ?? []).includes('reserve_plus');
  const cost = stageStaminaCost(1);
  const meter = (v: number, m: number) => `<div class="meter"><i style="width:${m ? Math.min(100, (v / m) * 100) : 0}%"></i></div>`;
  const row = (k: string, v: string) => `<div><dt>${esc(k)}</dt><dd>${v}</dd></div>`;
  const full = (t: number | null, v: number, m: number) => (v >= m ? '満タン' : t == null ? '—' : `${left(t - now)}（${at(t)}）`);
  return `<div class="sti">
    <div class="sti-now">
      <div class="sti-part"><span class="sti-k">本体</span><b>${fmt(st.value)}<small>/${fmt(st.max)}</small></b>${meter(st.value, st.max)}</div>
      <div class="sti-part"><span class="sti-k">予備</span><b>${fmt(st.reserve)}<small>/${fmt(st.reserveMax)}</small></b>${meter(st.reserve, st.reserveMax)}</div>
    </div>
    <dl class="sti-kv">
      ${row('出撃できる回数', `${fmt(Math.floor((st.value + st.reserve) / cost))}回<small>（1戦 ${cost}）</small>`)}
      ${row('回復の速さ', f.fast ? `${Math.round(f.per / 1000)}秒に1<small>（月パス 3倍速）</small>` : `${S.regenMinutes}分に1`)}
      ${row('次の回復', st.nextAt ? `あと ${mmss(st.nextAt - now)}` : '満タン')}
      ${row('今日あと回復する量', `${fmt(f.todayLeft)}<small>（0時まで）</small>`)}
      ${f.fast ? row('今日の回復（月パス）', `${fmt(f.gotToday)}<small>/${fmt(f.cap)}</small>`) : ''}
      ${row('本体が満タンまで', full(f.mainFull, st.value, st.max))}
      ${row('予備が満タンまで', full(f.reserveFull, st.reserve, st.reserveMax))}
    </dl>
    <h3 class="sti-h">上限の内訳</h3>
    <dl class="sti-kv sti-break">
      ${row('本体', `${fmt(S.baseMax)}<small>＋主Lv ${fmt(levelTier(levelNow()))}＋兵糧庫 ${fmt(kura * META_CONFIG.village.hyorograMaxPerLevel)}</small>＝${fmt(st.max)}`)}
      ${row('予備', `${fmt(S.reserveMax)}<small>＋兵糧庫 ${fmt(kura * META_CONFIG.village.hyorograReservePerLevel)}${paid ? `＋予備枠 ${fmt(S.paidReserveBonus)}` : ''}</small>＝${fmt(st.reserveMax)}`)}
    </dl>
  </div>`;
}

/** 兵糧の小窓（開いているあいだ1秒ごとに数え直す） */
export function openStaminaInfo() {
  if (!state.stamina) return;
  const m = openModal(html(Date.now()), { title: `兵糧`, className: 'stamina-info-wrap', onClose: () => clearInterval(timer) });
  m.el.querySelector('.modal-head h2')?.insertAdjacentHTML('afterbegin', resIcon('hyoro'));
  const timer = setInterval(() => {
    if (!m.el.isConnected || !state.stamina) return clearInterval(timer);
    m.body.innerHTML = html(Date.now());
  }, 1000);
}
